import { useState } from "react";
import { adminApi } from "../lib/api";
import { toast } from "sonner";
import { ShieldAlert, Search, Undo2, Loader2 } from "lucide-react";

/**
 * Admin tool: scan Customer-Support leads that were auto-routed by the
 * old SUBSTRING keyword matcher (e.g. "eta" inside "details") and offer
 * a 1-click bulk revert to Sales.
 *
 * Yash, Apr 28 2026 — backfill cleanup for the iteration-102 fix.
 */
export default function MisroutedCsAuditCard() {
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const runScan = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await adminApi.auditMisroutedCs(500, 5);
      setResults(res.data);
      const candidates = res.data?.candidates || [];
      if (candidates.length === 0) {
        toast.success(`Scanned ${res.data.scanned} CS leads — no misroutes found.`);
      } else {
        toast.info(`Found ${candidates.length} likely misrouted leads (scanned ${res.data.scanned})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const candidates = results?.candidates || [];
    if (selected.size === candidates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(candidates.map((c) => c.lead_id)));
    }
  };

  const revert = async () => {
    const ids = [...selected];
    if (!ids.length) {
      toast.error("Select at least one lead to revert.");
      return;
    }
    if (!window.confirm(`Revert ${ids.length} lead(s) back to Sales? This will:\n• Move them out of Customer Support\n• Clear the auto-router CS tags\n• Drop an internal audit note on each chat\n\nProceed?`)) return;
    setReverting(true);
    try {
      const res = await adminApi.revertMisroutedCs(ids);
      toast.success(`Reverted ${res.data.reverted}/${res.data.requested} leads back to Sales.`);
      // Drop reverted ones from view
      setResults((prev) => prev ? {
        ...prev,
        candidates: (prev.candidates || []).filter((c) => !selected.has(c.lead_id)),
      } : prev);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally {
      setReverting(false);
    }
  };

  const candidates = results?.candidates || [];

  return (
    <div className="bento-card" data-testid="misrouted-cs-audit-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2 bg-orange-50 rounded-md">
          <ShieldAlert size={20} className="text-orange-600" />
        </div>
        <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>
          Misrouted CS Leads Audit
        </h2>
      </div>
      <p className="text-sm text-[#78716C] mb-4 ml-12">
        Find Customer-Support chats that were auto-tagged by a substring keyword match (e.g. "eta" inside "details", "rto" inside "Porto") and revert them to Sales.
      </p>

      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="misrouted-cs-scan-btn"
          onClick={runScan}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading ? "Scanning…" : "Scan recent CS leads"}
        </button>
        {results && (
          <span className="text-xs text-[#78716C]" data-testid="misrouted-cs-summary">
            Scanned <strong className="text-[#1C1917]">{results.scanned}</strong> · Found <strong className={candidates.length ? "text-orange-700" : "text-emerald-700"}>{candidates.length}</strong> misroute{candidates.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {candidates.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-2 px-2 py-1.5 bg-orange-50 border border-orange-200 rounded-md">
            <label className="flex items-center gap-2 text-xs font-semibold text-[#1C1917] cursor-pointer">
              <input
                data-testid="misrouted-cs-select-all"
                type="checkbox"
                checked={selected.size === candidates.length}
                onChange={toggleAll}
                className="rounded"
              />
              Select all ({candidates.length})
            </label>
            <button
              data-testid="misrouted-cs-revert-btn"
              onClick={revert}
              disabled={!selected.size || reverting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-md text-xs font-bold transition"
            >
              {reverting ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
              Revert {selected.size} to Sales
            </button>
          </div>

          <div className="border border-[#E7E5E4] rounded-md max-h-[480px] overflow-y-auto" data-testid="misrouted-cs-list">
            {candidates.map((c) => (
              <div
                key={c.lead_id}
                className={`flex items-start gap-3 px-3 py-2.5 border-b border-[#E7E5E4] last:border-b-0 ${selected.has(c.lead_id) ? "bg-orange-50/60" : "hover:bg-gray-50"}`}
                data-testid={`misrouted-cs-row-${c.lead_id}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.lead_id)}
                  onChange={() => toggle(c.lead_id)}
                  className="mt-1 rounded"
                  data-testid={`misrouted-cs-checkbox-${c.lead_id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-[#1C1917] truncate">{c.name || "—"}</span>
                    <span className="text-xs text-[#78716C]">·</span>
                    <span className="text-xs text-[#78716C] font-mono">{c.phone}</span>
                  </div>
                  {c.ghost_matches.map((g, i) => (
                    <div key={`${g.keyword || ''}-${g.tag || ''}-${i}`} className="text-[11px] mb-1">
                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 font-semibold rounded">
                        ghost: "{g.keyword}"
                      </span>
                      <span className="text-[#78716C]"> → tagged as </span>
                      <span className="px-1.5 py-0.5 bg-gray-100 text-[#1C1917] font-medium rounded">{g.tag}</span>
                      <div className="text-[#44403C] italic mt-0.5 truncate">"{g.message}"</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
